import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createStockAudit,
  deleteStockAudit,
  getStockAuditById,
  getStockAuditFormData,
  getStockAuditItems,
  getStockAudits,
  updateStockAudit,
  updateStockAuditItems,
  validateStockAudit,
} from "../../services/stockAuditService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const defaultFormData = {
  company_id: 1,
  store_id: "",
  warehouse_id: "",
  audit_date: "",
  status: "draft",
  notes: "",
  created_by: 1,
  validated_by: "",
}


// =========================================================
// HELPERS
// =========================================================

function toNumber(value) {
  const number =
    Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatQuantity(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


function formatDateTime(value) {
  if (!value) {
    return "-"
  }

  const date =
    new Date(value)

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-"
  }

  return date.toLocaleString(
    "fr-FR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  )
}


function getStatusConfig(value) {
  const normalized =
    String(
      value || ""
    ).toLowerCase()

  if (
    normalized === "validated"
  ) {
    return {
      label: "Validé",
      color: "success",
    }
  }

  if (
    normalized === "submitted"
  ) {
    return {
      label: "Soumis",
      color: "warning",
    }
  }

  if (
    normalized === "draft"
  ) {
    return {
      label: "Brouillon",
      color: "info",
    }
  }

  if (
    normalized === "cancelled" ||
    normalized === "canceled"
  ) {
    return {
      label: "Annulé",
      color: "error",
    }
  }

  return {
    label:
      value || "-",
    color: "default",
  }
}


function getStatusChip(value) {
  const config =
    getStatusConfig(value)

  return (
    <Chip
      size="small"
      color={config.color}
      label={config.label}
      sx={{
        height: 24,
        fontWeight: 900,
      }}
    />
  )
}


function getAuditLocation(row) {
  if (
    row?.store_id ||
    row?.store_name
  ) {
    return {
      type: "store",
      typeLabel: "Shop",
      name:
        row?.store_name ||
        "-",
    }
  }

  if (
    row?.warehouse_id ||
    row?.warehouse_name
  ) {
    return {
      type: "warehouse",
      typeLabel: "Entrepôt",
      name:
        row?.warehouse_name ||
        "-",
    }
  }

  return {
    type: "",
    typeLabel: "-",
    name: "-",
  }
}


function getLocationChip(type) {
  if (
    type === "store"
  ) {
    return (
      <Chip
        size="small"
        label="Shop"
        color="primary"
        variant="outlined"
        sx={{
          height: 24,
          fontWeight: 800,
        }}
      />
    )
  }

  if (
    type === "warehouse"
  ) {
    return (
      <Chip
        size="small"
        label="Entrepôt"
        color="secondary"
        variant="outlined"
        sx={{
          height: 24,
          fontWeight: 800,
        }}
      />
    )
  }

  return (
    <Chip
      size="small"
      label="-"
      variant="outlined"
      sx={{
        height: 24,
      }}
    />
  )
}


function getAuditRowClass(status) {
  const normalized =
    String(
      status || ""
    ).toLowerCase()

  if (
    normalized === "validated"
  ) {
    return "stock-audit-row stock-audit-row--validated"
  }

  if (
    normalized === "submitted"
  ) {
    return "stock-audit-row stock-audit-row--submitted"
  }

  if (
    normalized === "draft"
  ) {
    return "stock-audit-row stock-audit-row--draft"
  }

  if (
    normalized === "cancelled" ||
    normalized === "canceled"
  ) {
    return "stock-audit-row stock-audit-row--cancelled"
  }

  return "stock-audit-row stock-audit-row--default"
}


function getVarianceTone(value) {
  const variance =
    toNumber(value)

  if (
    variance > 0
  ) {
    return {
      color: "#15803d",
      background:
        "rgba(22, 163, 74, 0.08)",
      border:
        "rgba(22, 163, 74, 0.18)",
      prefix: "+",
    }
  }

  if (
    variance < 0
  ) {
    return {
      color: "#dc2626",
      background:
        "rgba(220, 38, 38, 0.08)",
      border:
        "rgba(220, 38, 38, 0.18)",
      prefix: "",
    }
  }

  return {
    color: "#475569",
    background:
      "#f8fafc",
    border:
      "rgba(148, 163, 184, 0.20)",
    prefix: "",
  }
}


function getErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.35,
          sm: 1.5,
        },

        py: 1.15,

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.1,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            display: "grid",
            placeItems: "center",

            borderRadius: 2.5,

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.7,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 19,
                  sm: 21,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",

                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="body2"
              sx={{
                minWidth: 0,

                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// MOBILE AUDIT CARD
// =========================================================

function AuditMobileCard({
  row,
  onEdit,
  onItems,
  onValidate,
  onDelete,
}) {
  const location =
    getAuditLocation(row)

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        boxShadow:
          String(
            row.status || ""
          ).toLowerCase() ===
          "validated"
            ? "inset 4px 0 0 #22c55e, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : String(
                row.status || ""
              ).toLowerCase() ===
              "submitted"
            ? "inset 4px 0 0 #f59e0b, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : "inset 4px 0 0 #0ea5e9, 0 5px 16px rgba(15, 23, 42, 0.025)",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,

          "&:last-child": {
            pb: 1.5,
          },
        }}
      >
        <Stack spacing={1.2}>
          <Box
            sx={{
              display: "flex",

              justifyContent:
                "space-between",

              alignItems:
                "flex-start",

              gap: 1,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                {row.audit_code ||
                  `AUD-${row.id}`}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {formatDateTime(
                  row.audit_date
                )}
              </Typography>
            </Box>

            {getStatusChip(
              row.status
            )}
          </Box>


          <Box
            sx={{
              display: "flex",

              alignItems: "center",

              flexWrap: "wrap",

              gap: 0.7,
            }}
          >
            {getLocationChip(
              location.type
            )}

            <Typography
              variant="body2"
              fontWeight={800}
              color="#334155"
            >
              {location.name}
            </Typography>
          </Box>


          {row.notes ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflowWrap:
                  "anywhere",
              }}
            >
              {row.notes}
            </Typography>
          ) : null}


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",
              },

              gap: 0.7,
            }}
          >
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() =>
                onEdit(
                  row.id
                )
              }
            >
              Modifier
            </Button>


            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                onItems(
                  row
                )
              }
            >
              Comptage
            </Button>


            <Button
              variant="outlined"
              color="success"
              size="small"
              disabled={
                row.status ===
                "validated"
              }
              onClick={() =>
                onValidate(
                  row
                )
              }
            >
              Valider
            </Button>


            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() =>
                onDelete(
                  row.id
                )
              }
            >
              Supprimer
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}


// =========================================================
// PAGE
// =========================================================

function StockAuditsPage() {
  const theme =
    useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    audits,
    setAudits,
  ] = useState([])

  const [
    stores,
    setStores,
  ] = useState([])

  const [
    warehouses,
    setWarehouses,
  ] = useState([])

  const [
    users,
    setUsers,
  ] = useState([])

  const [
    statuses,
    setStatuses,
  ] = useState([])


  // =======================================================
  // MAIN DIALOG
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedAudit,
    setSelectedAudit,
  ] = useState(null)

  const [
    formData,
    setFormData,
  ] = useState({
    ...defaultFormData,
  })


  // =======================================================
  // ITEMS DIALOG
  // =======================================================

  const [
    itemsOpen,
    setItemsOpen,
  ] = useState(false)

  const [
    itemsLoading,
    setItemsLoading,
  ] = useState(false)

  const [
    itemsSaving,
    setItemsSaving,
  ] = useState(false)

  const [
    auditItems,
    setAuditItems,
  ] = useState([])

  const [
    selectedItemsAudit,
    setSelectedItemsAudit,
  ] = useState(null)


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize:
      ROWS_PER_PAGE,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message:
        String(
          message || ""
        ),
    })
  }


  const handleCloseSnackbar =
    () => {
      setSnackbar(
        (previous) => ({
          ...previous,
          open: false,
        })
      )
    }


  // =======================================================
  // LOAD AUDITS
  // =======================================================

  const loadAudits =
    async () => {
      try {
        setLoading(true)

        const data =
          await getStockAudits()

        setAudits(
          Array.isArray(data)
            ? data
            : []
        )
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger les audits de stock."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  // =======================================================
  // LOAD FORM DATA
  // =======================================================

  const loadFormData =
    async () => {
      try {
        const data =
          await getStockAuditFormData()

        setStores(
          Array.isArray(
            data?.stores
          )
            ? data.stores
            : []
        )

        setWarehouses(
          Array.isArray(
            data?.warehouses
          )
            ? data.warehouses
            : []
        )

        setUsers(
          Array.isArray(
            data?.users
          )
            ? data.users
            : []
        )

        setStatuses(
          Array.isArray(
            data?.statuses
          )
            ? data.statuses
            : []
        )
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger les données du formulaire."
          ),
          "error"
        )
      }
    }


  useEffect(() => {
    loadAudits()
    loadFormData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // =======================================================
  // FORM
  // =======================================================

  const resetForm = () => {
    setSelectedAudit(
      null
    )

    setFormData({
      ...defaultFormData,
    })
  }


  const openAddDialog =
    () => {
      resetForm()
      setOpen(true)
    }


  const openEditDialog =
    async (
      id
    ) => {
      try {
        const item =
          await getStockAuditById(
            id
          )

        setSelectedAudit(
          item
        )

        setFormData({
          company_id:
            item.company_id ??
            1,

          store_id:
            item.store_id ??
            "",

          warehouse_id:
            item.warehouse_id ??
            "",

          audit_date:
            item.audit_date
              ? new Date(
                  item.audit_date
                )
                  .toISOString()
                  .slice(
                    0,
                    16
                  )
              : "",

          status:
            item.status ||
            "draft",

          notes:
            item.notes ||
            "",

          created_by:
            item.created_by ??
            1,

          validated_by:
            item.validated_by ??
            "",
        })

        setOpen(true)
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger l’audit."
          ),
          "error"
        )
      }
    }


  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(false)
    resetForm()
  }


  const handleChange =
    (event) => {
      const {
        name,
        value,
      } =
        event.target

      if (
        name === "store_id"
      ) {
        setFormData(
          (previous) => ({
            ...previous,

            store_id:
              value === ""
                ? ""
                : Number(value),

            warehouse_id:
              "",
          })
        )

        return
      }


      if (
        name ===
        "warehouse_id"
      ) {
        setFormData(
          (previous) => ({
            ...previous,

            warehouse_id:
              value === ""
                ? ""
                : Number(value),

            store_id:
              "",
          })
        )

        return
      }


      setFormData(
        (previous) => ({
          ...previous,

          [name]:
            [
              "company_id",
              "created_by",
              "validated_by",
            ].includes(name)
              ? value === ""
                ? ""
                : Number(value)
              : value,
        })
      )
    }


  // =======================================================
  // VALIDATION
  // =======================================================

  const validateForm = () => {
    if (
      !formData.store_id &&
      !formData.warehouse_id
    ) {
      showSnackbar(
        "Sélectionnez un shop ou un entrepôt.",
        "warning"
      )

      return false
    }


    if (
      formData.store_id &&
      formData.warehouse_id
    ) {
      showSnackbar(
        "Sélectionnez un seul emplacement.",
        "warning"
      )

      return false
    }


    if (
      !formData.audit_date
    ) {
      showSnackbar(
        "La date de l’audit est obligatoire.",
        "warning"
      )

      return false
    }

    return true
  }


  // =======================================================
  // SAVE AUDIT
  // =======================================================

  const handleSave =
    async () => {
      if (
        !validateForm()
      ) {
        return
      }

      const payload = {
        company_id:
          Number(
            formData.company_id ||
              1
          ),

        store_id:
          formData.store_id ===
          ""
            ? null
            : Number(
                formData.store_id
              ),

        warehouse_id:
          formData.warehouse_id ===
          ""
            ? null
            : Number(
                formData.warehouse_id
              ),

        audit_date:
          formData.audit_date,

        status:
          formData.status ||
          "draft",

        notes:
          formData.notes
            ?.trim() ||
          null,

        created_by:
          Number(
            formData.created_by ||
              1
          ),

        validated_by:
          formData.validated_by ===
          ""
            ? null
            : Number(
                formData.validated_by
              ),
      }

      try {
        setSaving(true)

        if (
          selectedAudit?.id
        ) {
          await updateStockAudit(
            selectedAudit.id,
            payload
          )

          showSnackbar(
            "Audit de stock modifié avec succès."
          )
        } else {
          await createStockAudit(
            payload
          )

          showSnackbar(
            "Audit de stock créé avec succès."
          )
        }

        /*
         * Fermeture directe :
         * handleClose() bloque lorsque saving === true.
         */
        setOpen(false)
        resetForm()

        await loadAudits()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible d’enregistrer l’audit de stock."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DELETE
  // =======================================================

  const handleDelete =
    async (
      id
    ) => {
      const confirmed =
        window.confirm(
          "Supprimer cet audit de stock ?"
        )

      if (!confirmed) {
        return
      }

      try {
        await deleteStockAudit(
          id
        )

        showSnackbar(
          "Audit de stock supprimé."
        )

        await loadAudits()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de supprimer l’audit de stock."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // AUDIT ITEMS
  // =======================================================

  const handleOpenItems =
    async (
      row
    ) => {
      try {
        setItemsLoading(true)

        setSelectedItemsAudit(
          row
        )

        const data =
          await getStockAuditItems(
            row.id
          )

        setAuditItems(
          Array.isArray(data)
            ? data
            : []
        )

        setItemsOpen(true)
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger les lignes de comptage."
          ),
          "error"
        )
      } finally {
        setItemsLoading(false)
      }
    }


  const handleCloseItems =
    () => {
      if (itemsSaving) {
        return
      }

      setItemsOpen(false)

      setSelectedItemsAudit(
        null
      )

      setAuditItems([])
    }


  const handleItemChange =
    (
      index,
      field,
      value
    ) => {
      setAuditItems(
        (previous) =>
          previous.map(
            (
              item,
              itemIndex
            ) => {
              if (
                itemIndex !==
                index
              ) {
                return item
              }

              const nextItem = {
                ...item,

                [field]:
                  field ===
                  "physical_quantity"
                    ? value ===
                      ""
                      ? ""
                      : Number(value)
                    : value,
              }

              const systemQty =
                Number(
                  nextItem.system_quantity ||
                    0
                )

              const physicalQty =
                Number(
                  nextItem.physical_quantity ||
                    0
                )

              nextItem.variance_quantity =
                Number(
                  (
                    physicalQty -
                    systemQty
                  ).toFixed(2)
                )

              return nextItem
            }
          )
      )
    }


  const handleSaveItems =
    async () => {
      if (
        !selectedItemsAudit?.id
      ) {
        return
      }

      try {
        setItemsSaving(true)

        const payload = {
          items:
            auditItems.map(
              (item) => ({
                product_id:
                  Number(
                    item.product_id
                  ),

                system_quantity:
                  Number(
                    item.system_quantity ||
                      0
                  ),

                physical_quantity:
                  Number(
                    item.physical_quantity ||
                      0
                  ),

                variance_quantity:
                  Number(
                    item.variance_quantity ||
                      0
                  ),

                comment:
                  item.comment
                    ?.trim() ||
                  null,
              })
            ),
        }

        await updateStockAuditItems(
          selectedItemsAudit.id,
          payload
        )

        showSnackbar(
          "Comptage physique enregistré avec succès."
        )

        /*
         * Fermeture directe :
         * handleCloseItems() bloque lorsque itemsSaving === true.
         */
        setItemsOpen(false)

        setSelectedItemsAudit(
          null
        )

        setAuditItems([])

        await loadAudits()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible d’enregistrer le comptage physique."
          ),
          "error"
        )
      } finally {
        setItemsSaving(false)
      }
    }


  // =======================================================
  // VALIDATE AUDIT
  // =======================================================

  const handleValidateAudit =
    async (
      row
    ) => {
      const confirmed =
        window.confirm(
          "Valider cet audit et appliquer la correction automatique du stock ?"
        )

      if (!confirmed) {
        return
      }

      try {
        await validateStockAudit(
          row.id,
          {
            validated_by: 1,
          }
        )

        showSnackbar(
          "Audit validé avec succès."
        )

        await loadAudits()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de valider l’audit."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // FILTER
  // =======================================================

  const filteredAudits =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return audits.filter(
        (item) => {
          const location =
            getAuditLocation(
              item
            )

          const matchesSearch =
            keyword === "" ||
            [
              item.audit_code,
              item.store_name,
              item.warehouse_name,
              item.status,
              getStatusConfig(
                item.status
              ).label,
              location.typeLabel,
              item.notes,
            ].some(
              (value) =>
                String(
                  value || ""
                )
                  .toLowerCase()
                  .includes(
                    keyword
                  )
            )

          const matchesStatus =
            statusFilter === "" ||
            String(
              item.status || ""
            ) ===
              statusFilter

          return (
            matchesSearch &&
            matchesStatus
          )
        }
      )
    }, [
      audits,
      search,
      statusFilter,
    ])


  // =======================================================
  // STATS
  // =======================================================

  const stats =
    useMemo(() => {
      return filteredAudits.reduce(
        (
          accumulator,
          item
        ) => {
          accumulator.total +=
            1

          const status =
            String(
              item.status || ""
            ).toLowerCase()

          if (
            status ===
            "draft"
          ) {
            accumulator.draft +=
              1
          }

          if (
            status ===
            "submitted"
          ) {
            accumulator.submitted +=
              1
          }

          if (
            status ===
            "validated"
          ) {
            accumulator.validated +=
              1
          }

          return accumulator
        },
        {
          total: 0,
          draft: 0,
          submitted: 0,
          validated: 0,
        }
      )
    }, [
      filteredAudits,
    ])


  // =======================================================
  // PAGINATION
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,

        page: 0,
        pageSize:
          ROWS_PER_PAGE,
      })
    )
  }, [
    search,
    statusFilter,
  ])


  const paginatedMobileRows =
    useMemo(() => {
      const start =
        paginationModel.page *
        ROWS_PER_PAGE

      return filteredAudits.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      filteredAudits,
      paginationModel.page,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredAudits.length /
            ROWS_PER_PAGE
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,

          pageSize:
            ROWS_PER_PAGE,
        })
      )
    }
  }, [
    filteredAudits.length,
    paginationModel.page,
  ])


  const resetFilters =
    () => {
      setSearch("")
      setStatusFilter("")

      setPaginationModel({
        page: 0,
        pageSize:
          ROWS_PER_PAGE,
      })
    }


  // =======================================================
  // DESKTOP COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field: "audit",

          headerName:
            "Audit",

          minWidth: 205,

          flex: 1.15,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.16}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontSize: "0.86rem",
                    fontWeight: 900,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.audit_code ||
                    ""
                  }
                >
                  {params.row.audit_code ||
                    `AUD-${params.row.id}`}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "#64748b",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  ID {params.row.id}
                </Typography>
              </Stack>
            ),
        },


        {
          field:
            "location",

          headerName:
            "Emplacement",

          minWidth: 230,

          flex: 1.35,

          sortable: false,

          renderCell:
            (params) => {
              const location =
                getAuditLocation(
                  params.row
                )

              return (
                <Box
                  sx={{
                    width: "100%",
                    minWidth: 0,

                    display: "flex",

                    alignItems: "center",

                    gap: 0.8,
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                    }}
                  >
                    {getLocationChip(
                      location.type
                    )}
                  </Box>

                  <Typography
                    sx={{
                      minWidth: 0,

                      color: "#334155",

                      fontSize: "0.83rem",

                      fontWeight: 800,

                      lineHeight: 1.25,

                      overflow: "hidden",

                      textOverflow:
                        "ellipsis",

                      whiteSpace: "nowrap",
                    }}

                    title={
                      location.name
                    }
                  >
                    {location.name}
                  </Typography>
                </Box>
              )
            },
        },


        {
          field:
            "audit_date",

          headerName:
            "Date audit",

          width: 155,

          renderCell:
            (params) => (
              <Typography
                sx={{
                  color: "#475569",
                  fontSize: "0.79rem",
                  fontWeight: 700,
                  lineHeight: 1.25,
                }}
              >
                {formatDateTime(
                  params.value
                )}
              </Typography>
            ),
        },


        {
          field:
            "status",

          headerName:
            "Statut",

          width: 120,

          renderCell:
            (params) =>
              getStatusChip(
                params.value
              ),
        },


        {
          field:
            "actions",

          headerName:
            "Actions",

          width: 330,

          sortable: false,

          filterable: false,

          disableColumnMenu:
            true,

          renderCell:
            (params) => (
              <Box
                sx={{
                  width: "100%",

                  display: "grid",

                  gridTemplateColumns:
                    "repeat(4, auto)",

                  gap: 0.55,

                  alignItems: "center",
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  disableElevation

                  onClick={() =>
                    openEditDialog(
                      params.row.id
                    )
                  }

                  sx={{
                    minWidth: 70,
                    px: 0.9,
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  Modifier
                </Button>


                <Button
                  size="small"
                  variant="outlined"

                  onClick={() =>
                    handleOpenItems(
                      params.row
                    )
                  }

                  sx={{
                    minWidth: 78,
                    px: 0.9,
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  Comptage
                </Button>


                <Button
                  size="small"
                  variant="outlined"
                  color="success"

                  disabled={
                    params.row.status ===
                    "validated"
                  }

                  onClick={() =>
                    handleValidateAudit(
                      params.row
                    )
                  }

                  sx={{
                    minWidth: 70,
                    px: 0.9,
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  Valider
                </Button>


                <Button
                  size="small"
                  variant="outlined"
                  color="error"

                  onClick={() =>
                    handleDelete(
                      params.row.id
                    )
                  }

                  sx={{
                    minWidth: 78,
                    px: 0.9,
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  Supprimer
                </Button>
              </Box>
            ),
        },
      ],
      []
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              minHeight: 40,

              borderRadius: 2.5,

              textTransform:
                "none",

              fontWeight: 800,

              boxShadow: "none",
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,

              bgcolor: "#ffffff",
            },

          "& .MuiChip-root":
            {
              fontWeight: 800,
            },

          "& .MuiPaper-root":
            {
              backgroundImage:
                "none",
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "minmax(0, 1fr) auto",
            },

            gap: 1.5,

            alignItems: "start",

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Audits de stock
            </Typography>


            <Typography
              variant="body2"
              color="text.secondary"

              sx={{
                mt: 0.4,

                lineHeight: 1.5,

                maxWidth: 820,
              }}
            >
              Contrôle physique du stock, analyse des écarts
              et corrections après validation.
            </Typography>
          </Box>


          <Button
            variant="contained"
            disableElevation

            onClick={
              openAddDialog
            }

            sx={{
              minWidth: {
                xs: "100%",
                sm: 170,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },
            }}
          >
            + Nouvel audit
          </Button>
        </Box>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"
          variant="outlined"

          sx={{
            mb: 1.6,

            py: 0.35,

            borderRadius: 3,
          }}
        >
          Le comptage compare la quantité système avec la
          quantité physique. Après validation de l’audit, les
          corrections de stock prévues par le backend peuvent
          être appliquées automatiquement.
        </Alert>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              md:
                "repeat(4, minmax(0, 1fr))",
            },

            gap: 1.1,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Audits"

            value={
              stats.total
            }

            caption="Audits affichés"

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <Inventory2RoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Brouillons"

            value={
              stats.draft
            }

            caption="Audits en préparation"

            accent="#0284c7"

            background="rgba(2, 132, 199, 0.09)"

            icon={
              <ScheduleRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Soumis"

            value={
              stats.submitted
            }

            caption="Audits à contrôler"

            accent="#d97706"

            background="rgba(217, 119, 6, 0.09)"

            icon={
              <WarningAmberRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Validés"

            value={
              stats.validated
            }

            caption="Audits finalisés"

            accent="#16a34a"

            background="rgba(22, 163, 74, 0.09)"

            icon={
              <CheckCircleRoundedIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 5px 16px rgba(15, 23, 42, 0.025)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                lg:
                  "minmax(0, 1fr) 220px 125px 135px",
              },

              gap: 1,

              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"

              label="Recherche"

              placeholder="Code, shop, entrepôt ou statut..."

              value={
                search
              }

              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }
            />


            <TextField
              select
              fullWidth
              size="small"

              label="Statut"

              value={
                statusFilter
              }

              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              {statuses.map(
                (status) => (
                  <MenuItem
                    key={
                      status.value
                    }

                    value={
                      status.value
                    }
                  >
                    {status.label}
                  </MenuItem>
                )
              )}
            </TextField>


            <Button
              variant="contained"
              disableElevation

              onClick={
                loadAudits
              }

              disabled={
                loading
              }

              sx={{
                minWidth: 0,
                width: "100%",
                whiteSpace:
                  "nowrap",
              }}
            >
              Actualiser
            </Button>


            <Button
              variant="outlined"

              onClick={
                resetFilters
              }

              sx={{
                minWidth: 0,
                width: "100%",
                whiteSpace:
                  "nowrap",
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            borderRadius: 3.5,

            overflow: "hidden",

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Registre des audits stock
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Emplacement, statut, date et opérations de
                contrôle physique
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${filteredAudits.length} audit${
                filteredAudits.length >
                1
                  ? "s"
                  : ""
              }`}
            />
          </Box>


          {/* =================================================
              DESKTOP
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",

                minWidth: 0,

                overflowX: "auto",

                bgcolor: "#ffffff",
              }}
            >
              <Box
                sx={{
                  minWidth: 1050,
                }}
              >
                <DataGrid
                  autoHeight

                  rows={
                    filteredAudits
                  }

                  columns={
                    columns
                  }

                  loading={
                    loading
                  }

                  disableRowSelectionOnClick

                  disableColumnSelector

                  pagination

                  paginationMode="client"

                  paginationModel={
                    paginationModel
                  }

                  onPaginationModelChange={(
                    model
                  ) =>
                    setPaginationModel({
                      page:
                        model.page,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  }

                  pageSizeOptions={[
                    ROWS_PER_PAGE,
                  ]}

                  rowHeight={70}

                  columnHeaderHeight={50}

                  getRowClassName={(
                    params
                  ) =>
                    getAuditRowClass(
                      params.row.status
                    )
                  }

                  localeText={{
                    noRowsLabel:
                      "Aucun audit de stock",

                    noResultsOverlayLabel:
                      "Aucun résultat",

                    MuiTablePagination:
                      {
                        labelRowsPerPage:
                          "",

                        labelDisplayedRows:
                          ({
                            from,
                            to,
                            count,
                          }) =>
                            `${from}–${to} sur ${count}`,
                      },
                  }}

                  sx={{
                    border: 0,

                    width: "100%",

                    color: "#334155",

                    fontSize: "0.83rem",

                    bgcolor:
                      "#ffffff",

                    "& .MuiDataGrid-columnHeaders":
                      {
                        bgcolor:
                          "#f8fafc",

                        borderBottom:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiDataGrid-columnHeader":
                      {
                        bgcolor:
                          "#f8fafc",

                        px: 1.25,

                        borderRight:
                          "1px solid rgba(226, 232, 240, 0.60)",
                      },

                    "& .MuiDataGrid-columnHeader:last-of-type":
                      {
                        borderRight:
                          "none",
                      },

                    "& .MuiDataGrid-columnHeaderTitle":
                      {
                        color:
                          "#334155",

                        fontSize:
                          "0.76rem",

                        fontWeight: 900,

                        letterSpacing:
                          "0.025em",
                      },

                    "& .MuiDataGrid-columnSeparator":
                      {
                        color:
                          "rgba(148, 163, 184, 0.18)",
                      },

                    "& .MuiDataGrid-cell":
                      {
                        px: 1.25,

                        minWidth: 0,

                        display: "flex",

                        alignItems:
                          "center",

                        overflow:
                          "hidden",

                        borderBottom:
                          "1px solid #edf2f7",

                        borderRight:
                          "1px solid rgba(241, 245, 249, 0.85)",
                      },

                    "& .MuiDataGrid-cell:last-of-type":
                      {
                        borderRight:
                          "none",
                      },

                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .stock-audit-row":
                      {
                        bgcolor:
                          "#ffffff",

                        transition:
                          "background-color 0.16s ease, box-shadow 0.16s ease",
                      },

                    "& .stock-audit-row--validated":
                      {
                        boxShadow:
                          "inset 4px 0 0 #22c55e",

                        bgcolor:
                          "rgba(240, 253, 244, 0.30)",
                      },

                    "& .stock-audit-row--submitted":
                      {
                        boxShadow:
                          "inset 4px 0 0 #f59e0b",

                        bgcolor:
                          "rgba(255, 251, 235, 0.36)",
                      },

                    "& .stock-audit-row--draft":
                      {
                        boxShadow:
                          "inset 4px 0 0 #0ea5e9",

                        bgcolor:
                          "rgba(240, 249, 255, 0.35)",
                      },

                    "& .stock-audit-row--cancelled":
                      {
                        boxShadow:
                          "inset 4px 0 0 #ef4444",

                        bgcolor:
                          "rgba(254, 242, 242, 0.30)",
                      },

                    "& .stock-audit-row--default":
                      {
                        boxShadow:
                          "inset 4px 0 0 #cbd5e1",
                      },

                    "& .stock-audit-row:hover":
                      {
                        bgcolor:
                          "#f8fbff !important",

                        boxShadow:
                          "inset 4px 0 0 #2563eb, 0 4px 14px rgba(15, 23, 42, 0.045)",
                      },

                    "& .MuiDataGrid-footerContainer":
                      {
                        minHeight: 52,

                        bgcolor:
                          "#fbfdff",

                        borderTop:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiTablePagination-toolbar":
                      {
                        minHeight: 52,

                        px: 1.25,
                      },

                    "& .MuiTablePagination-displayedRows":
                      {
                        color:
                          "#475569",

                        fontSize:
                          "0.78rem",

                        fontWeight: 800,
                      },

                    "& .MuiTablePagination-selectLabel":
                      {
                        display: "none",
                      },

                    "& .MuiTablePagination-input":
                      {
                        display: "none",
                      },

                    "& .MuiDataGrid-virtualScroller":
                      {
                        overflowX:
                          "auto",
                      },

                    "& .MuiDataGrid-scrollbar--horizontal":
                      {
                        height:
                          "8px",
                      },
                  }}
                />
              </Box>
            </Box>
          ) : (

            /* =================================================
               MOBILE / TABLET
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.1,
                  sm: 1.25,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des audits...
                  </Typography>
                </Box>
              ) : paginatedMobileRows.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucun audit trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedMobileRows.map(
                    (row) => (
                      <AuditMobileCard
                        key={
                          row.id
                        }

                        row={
                          row
                        }

                        onEdit={
                          openEditDialog
                        }

                        onItems={
                          handleOpenItems
                        }

                        onValidate={
                          handleValidateAudit
                        }

                        onDelete={
                          handleDelete
                        }
                      />
                    )
                  )}
                </Stack>
              )}


              <TablePagination
                component="div"

                count={
                  filteredAudits.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor:
                    "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          AUDIT DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }

        onClose={
          handleClose
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            m: {
              xs: 0,
              sm: 2,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "92dvh",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.3,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {selectedAudit
              ? "Modifier l’audit de stock"
              : "Nouvel audit de stock"}
          </Typography>


          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.3,
            }}
          >
            {selectedAudit
              ? "Modifiez l’emplacement, la date, le statut ou les notes."
              : "Créez un audit physique pour un shop ou un entrepôt."}
          </Typography>
        </DialogTitle>


        <DialogContent dividers>
          <Stack
            spacing={2}

            sx={{
              pt: 0.5,
            }}
          >

            {/* LOCATION */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 0.45,
                }}
              >
                Emplacement à auditer
              </Typography>


              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mb: 1.2,
                }}
              >
                Sélectionnez soit un shop, soit un entrepôt.
                Le choix de l’un efface automatiquement l’autre.
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"

                  label="Shop"

                  name="store_id"

                  value={
                    formData.store_id
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="">
                    Aucun
                  </MenuItem>

                  {stores.map(
                    (store) => (
                      <MenuItem
                        key={
                          store.id
                        }

                        value={
                          store.id
                        }
                      >
                        {store.code} -{" "}
                        {store.name}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  select
                  size="small"

                  label="Entrepôt"

                  name="warehouse_id"

                  value={
                    formData.warehouse_id
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="">
                    Aucun
                  </MenuItem>

                  {warehouses.map(
                    (warehouse) => (
                      <MenuItem
                        key={
                          warehouse.id
                        }

                        value={
                          warehouse.id
                        }
                      >
                        {warehouse.code} -{" "}
                        {warehouse.name}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Box>
            </Paper>


            {/* DATE + STATUS */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                bgcolor: "#f8fafc",

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Audit et validation
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.5,

                  mb: 1.5,
                }}
              >
                <TextField
                  type="datetime-local"

                  size="small"

                  label="Date audit"

                  name="audit_date"

                  value={
                    formData.audit_date
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }

                  InputLabelProps={{
                    shrink: true,
                  }}
                />


                <TextField
                  select
                  size="small"

                  label="Statut"

                  name="status"

                  value={
                    formData.status
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  {statuses.map(
                    (status) => (
                      <MenuItem
                        key={
                          status.value
                        }

                        value={
                          status.value
                        }
                      >
                        {status.label}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Box>


              <TextField
                select
                size="small"

                label="Validé par"

                name="validated_by"

                value={
                  formData.validated_by
                }

                onChange={
                  handleChange
                }

                fullWidth

                disabled={
                  saving
                }
              >
                <MenuItem value="">
                  Aucun
                </MenuItem>

                {users.map(
                  (user) => (
                    <MenuItem
                      key={
                        user.id
                      }

                      value={
                        user.id
                      }
                    >
                      {user.label}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Paper>


            {/* NOTES */}

            <TextField
              label="Notes"

              name="notes"

              value={
                formData.notes
              }

              onChange={
                handleChange
              }

              fullWidth

              multiline

              minRows={3}

              disabled={
                saving
              }

              placeholder="Informations utiles sur le contrôle physique..."
            />
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleClose
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>


          <Button
            variant="contained"
            disableElevation

            onClick={
              handleSave
            }

            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedAudit
              ? "Modifier"
              : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          PHYSICAL COUNT DIALOG
      ================================================= */}

      <Dialog
        open={
          itemsOpen
        }

        onClose={
          handleCloseItems
        }

        fullWidth

        maxWidth="lg"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            m: {
              xs: 0,
              sm: 2,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "94dvh",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.3,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            Comptage physique
            {selectedItemsAudit?.audit_code
              ? ` — ${selectedItemsAudit.audit_code}`
              : ""}
          </Typography>


          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.3,
            }}
          >
            Comparez le stock système avec la quantité
            réellement comptée. L’écart est recalculé
            automatiquement.
          </Typography>
        </DialogTitle>


        <DialogContent dividers>
          {itemsLoading ? (
            <Alert
              severity="info"
              variant="outlined"
            >
              Chargement des lignes d’audit...
            </Alert>
          ) : auditItems.length ===
            0 ? (
            <Paper
              variant="outlined"

              sx={{
                p: 4,

                borderRadius: 3,

                textAlign: "center",
              }}
            >
              <Typography
                color="text.secondary"
              >
                Aucune ligne trouvée pour cet audit.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.2}>
              {auditItems.map(
                (
                  item,
                  index
                ) => {
                  const varianceTone =
                    getVarianceTone(
                      item.variance_quantity
                    )

                  return (
                    <Paper
                      key={`${item.product_id}-${index}`}

                      variant="outlined"

                      sx={{
                        p: {
                          xs: 1.3,
                          sm: 1.5,
                        },

                        borderRadius: 3,

                        borderColor:
                          "rgba(148, 163, 184, 0.22)",

                        boxShadow:
                          "0 4px 14px rgba(15, 23, 42, 0.025)",
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Box
                          sx={{
                            display:
                              "flex",

                            flexDirection: {
                              xs: "column",
                              sm: "row",
                            },

                            justifyContent:
                              "space-between",

                            alignItems: {
                              xs: "flex-start",
                              sm: "center",
                            },

                            gap: 0.7,
                          }}
                        >
                          <Box>
                            <Typography
                              fontWeight={900}
                              color="#0f172a"
                            >
                              {item.product_name ||
                                "-"}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.product_code ||
                                "-"}
                            </Typography>
                          </Box>


                          <Box
                            sx={{
                              px: 1.1,
                              py: 0.55,

                              minWidth: 92,

                              borderRadius: 2,

                              textAlign:
                                "center",

                              color:
                                varianceTone.color,

                              bgcolor:
                                varianceTone.background,

                              border:
                                "1px solid",

                              borderColor:
                                varianceTone.border,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display:
                                  "block",

                                fontWeight:
                                  800,

                                lineHeight:
                                  1.1,
                              }}
                            >
                              Écart
                            </Typography>

                            <Typography
                              fontWeight={900}
                              sx={{
                                lineHeight:
                                  1.2,
                              }}
                            >
                              {
                                varianceTone.prefix
                              }
                              {formatQuantity(
                                item.variance_quantity
                              )}
                            </Typography>
                          </Box>
                        </Box>


                        <Box
                          sx={{
                            display:
                              "grid",

                            gridTemplateColumns: {
                              xs: "1fr",

                              md:
                                "repeat(3, minmax(0, 1fr))",
                            },

                            gap: 1,
                          }}
                        >
                          <TextField
                            size="small"

                            label="Quantité système"

                            value={formatQuantity(
                              item.system_quantity
                            )}

                            fullWidth

                            InputProps={{
                              readOnly: true,
                            }}
                          />


                          <TextField
                            size="small"

                            label="Quantité physique"

                            type="number"

                            value={
                              item.physical_quantity
                            }

                            onChange={(
                              event
                            ) =>
                              handleItemChange(
                                index,
                                "physical_quantity",
                                event.target.value
                              )
                            }

                            fullWidth

                            disabled={
                              itemsSaving
                            }

                            slotProps={{
                              htmlInput:
                                {
                                  min: 0,
                                  step: 0.01,
                                },
                            }}
                          />


                          <TextField
                            size="small"

                            label="Écart calculé"

                            value={formatQuantity(
                              item.variance_quantity
                            )}

                            fullWidth

                            InputProps={{
                              readOnly: true,
                            }}

                            sx={{
                              "& .MuiInputBase-input":
                                {
                                  color:
                                    varianceTone.color,

                                  fontWeight:
                                    900,
                                },
                            }}
                          />
                        </Box>


                        <TextField
                          size="small"

                          label="Commentaire"

                          value={
                            item.comment ||
                            ""
                          }

                          onChange={(
                            event
                          ) =>
                            handleItemChange(
                              index,
                              "comment",
                              event.target.value
                            )
                          }

                          fullWidth

                          multiline

                          minRows={2}

                          disabled={
                            itemsSaving
                          }

                          placeholder="Justification de l’écart, casse, erreur de saisie, perte..."
                        />
                      </Stack>
                    </Paper>
                  )
                }
              )}
            </Stack>
          )}
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleCloseItems
            }

            disabled={
              itemsSaving
            }
          >
            Annuler
          </Button>


          <Button
            variant="contained"
            disableElevation

            onClick={
              handleSaveItems
            }

            disabled={
              itemsSaving ||
              itemsLoading ||
              auditItems.length ===
                0
            }
          >
            {itemsSaving
              ? "Enregistrement..."
              : "Enregistrer le comptage"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          3500
        }

        onClose={
          handleCloseSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={
            handleCloseSnackbar
          }

          severity={
            snackbar.severity
          }

          variant="filled"

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default StockAuditsPage
